import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"
import { Menu, X } from "lucide-react";

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const [open, setOpen] = React.useState(false);

  // Collect all tab triggers
  const tabTriggers = React.Children.toArray(children);

  return (
    <>
      {/* Desktop: show tab row (force hidden on mobile regardless of passed className) */}
      <div className={cn("hidden sm:flex w-full overflow-x-auto")}> 
        <TabsPrimitive.List ref={ref} className={className} {...props}>
          {children}
        </TabsPrimitive.List>
      </div>
      {/* Mobile: hamburger menu */}
      <div className="flex sm:hidden w-full items-center">
        {!open ? (
          <button
            className="ml-auto p-2"
            aria-label="Open tab menu"
            onClick={() => setOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
        ) : (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-end">
            <div className="bg-background w-64 max-w-full h-full shadow-lg p-4 flex flex-col">
              <button
                className="ml-auto mb-4 p-2"
                aria-label="Close tab menu"
                onClick={() => setOpen(false)}
              >
                <X className="w-6 h-6" />
              </button>
              <TabsPrimitive.List ref={ref} className={cn("flex flex-col gap-2", className)} {...props}>
                {React.Children.map(tabTriggers, (child) => {
                  if (React.isValidElement(child)) {
                    return React.cloneElement(child, {
                      ...child.props,
                      onClick: (e: React.MouseEvent) => {
                        child.props?.onClick?.(e);
                        setOpen(false);
                      }
                    });
                  }
                  return child;
                })}
              </TabsPrimitive.List>
            </div>
            {/* Click outside to close */}
            <div className="flex-1" onClick={() => setOpen(false)} />
          </div>
        )}
      </div>
    </>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm min-h-[44px] gap-1",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
